import {
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'

import useMakeRequestWithStatusHandlers from 'app/hooks/useMakeRequestWithStatusHandlers'
import requests from 'app/requests/Order/ContainersBooking.requests'
import Button from 'app/shared-components/Button'
import SkyNetTable from 'app/shared-components/SkyNetTable'
import {
    SelectedContainer,
} from 'app/Apps/OrderManagement/Orders/orders.types'

import useContainerManagementRoutes from 'app/SkyNetRoutes/hooks/useContainerManagementRoutes'
import simpleApi from 'app/services/simpleApi'
import getConfig from './AvailableContainers.config'
import useStyles from './AvailableContainers.styles'
import getSelectedContainers from './getSelectedContainers'

type Props = {
    location?: number | string,
    selectedContainers?: SelectedContainer[],
    requiredContainers?: Record<string, any>[],
    exists: boolean,
    orderNumber?: string,
    orderVersion?: number,
    disabled?: boolean,
    onSuccess?: (...args: any) => void,
    isInternalType?: boolean,
    isPreSelectContainers?: boolean,
}

const defaultProps = {
    location: undefined,
    orderNumber: undefined,
    orderVersion: undefined,
    disabled: false,
    isInternalType: false,
    isPreSelectContainers: true,
    requiredContainers: [],
    selectedContainers: [],
    onSuccess: noop,
}

const AvailableContainers = ({
    location,
    isInternalType,
    selectedContainers,
    isPreSelectContainers,
    requiredContainers,
    orderNumber,
    orderVersion,
    onSuccess,
    disabled,
    exists,
}: Props) => {
    const {
        classes,
    } = useStyles()

    const {
        Containers: ContainersRoutes,
    } = useContainerManagementRoutes()

    const [
        value,
        onChange,
    ] = useState([])

    const [
        derivedLocation,
        setLocation,
    ] = useState(location)

    const [
        previousSelected,
        setPreviousSelected,
    ] = useState()

    const [
        containers,
        setContainers,
    ] = useState([])

    useEffect(() => {
        if (derivedLocation !== location) {
            onChange([])
            setLocation(location)
        }
    }, [
        derivedLocation,
        location,
        onChange,
    ])

    const onSelect = useCallback((selectedIds) => {
        onChange(selectedIds)
    }, [onChange])

    const addContainersApi = useMakeRequestWithStatusHandlers({
        name: 'SelectedContainer',
        api: simpleApi,
        method: 'POST',
        onSuccess,
        domainName: 'SelectedContainer',
    })

    const addToBookedContainers = useCallback(() => {
        return addContainersApi({
            data: {
                containers: value.map((containerId) => {
                    return {
                        container: {
                            id: containerId,
                        },
                    }
                }),
                version: orderVersion,
            },
            url: requests.addSelectedContainers({
                orderNumber,
            }).url,
        })
    }, [
        addContainersApi,
        orderNumber,
        orderVersion,
        value,
    ])

    const onLoad = useCallback(({
        data,
    }) => {
        setContainers(data)
    }, [])

    useEffect(() => {
        // preselection does not support product types (CT)
        if (!isPreSelectContainers || !isInternalType
                || (!containers?.length || !requiredContainers?.length)) {
            return
        }

        const preSelectedContainers = getSelectedContainers(
            requiredContainers,
            selectedContainers,
            containers,
        )

        if (!isEqual(preSelectedContainers, previousSelected)) {
            setPreviousSelected(preSelectedContainers)
            onSelect(preSelectedContainers)
        }
    },
    [
        containers,
        onSelect,
        previousSelected,
        selectedContainers,
        requiredContainers,
        isPreSelectContainers,
        isInternalType,
    ])

    const tableConfig = useMemo(() => {
        return getConfig({
            availableContainersLinkConfig: {
                path: ContainersRoutes.Overview.path,
                params: {
                    key: 'serialNumber',
                },
            },
        })
    }, []) // eslint-disable-line

    const urlConfig = useMemo(() => {
        return requests.getAvailableContainers({
            orderNumber,
        })
    }, [orderNumber])

    return (
        <>
            {location !== undefined ? (
                <SkyNetTable
                    name="ContainerView"
                    selectedRows={value}
                    onSelectRow={!disabled && onSelect}
                    simple
                    uniqField="id"
                    customUrl={urlConfig.url}
                    method={urlConfig.method}
                    className={classes.root}
                    onLoad={onLoad}
                    rowsPerPageDefault={250}
                    tableConfig={tableConfig}
                    showSearch
                />
            ) : (
                <div className={classes.emptyMsg}>
                    Please select a location.
                </div>
            )}
            {location !== undefined && exists && !disabled && (
                <div className={classes.buttonContainer}>
                    <Button
                        label="add to booked containers"
                        name="add"
                        disabled={!value.length}
                        onClick={addToBookedContainers}
                    />
                </div>
            )}
        </>
    )
}

AvailableContainers.defaultProps = defaultProps

export default AvailableContainers
